import { createContext, useContext, ReactNode, useState } from 'react';
import { useLocalStorage } from '@shared/hooks/useLocalStorage';
import { CartItem } from '../types';
import '@features/wishlist/context/WishlistToast.css';

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: Omit<CartItem, 'id'>) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  getCartItemCount: () => number;
  getCartTotal: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

/**
 * Generate a unique cart item ID based on product ID, color, and size
 */
const generateCartItemId = (productId: string, color?: string, size?: string): string => {
  const parts = [productId];
  if (color) parts.push(`color:${color}`);
  if (size) parts.push(`size:${size}`);
  return parts.join('|');
};

export const CartProvider = ({ children }: CartProviderProps) => {
  const [cartItems, setCartItems] = useLocalStorage<CartItem[]>('cart', []);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  const addToCart = (item: Omit<CartItem, 'id'>) => {
    const itemId = generateCartItemId(item.productId, item.color, item.size);
    
    setCartItems((currentItems) => {
      const existingItemIndex = currentItems.findIndex((cartItem) => cartItem.id === itemId);

      if (existingItemIndex >= 0) {
        // Item already exists, update quantity
        return currentItems.map((cartItem, index) =>
          index === existingItemIndex
            ? { ...cartItem, quantity: cartItem.quantity + item.quantity }
            : cartItem
        );
      } else {
        // New item, add to cart
        return [...currentItems, { ...item, id: itemId }];
      }
    });
    showToast('Product Added to cart');
  };

  const removeFromCart = (id: string) => {
    setCartItems((currentItems) => currentItems.filter((item) => item.id !== id));
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }

    setCartItems((currentItems) =>
      currentItems.map((item) => (item.id === id ? { ...item, quantity } : item))
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartItemCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartItemCount,
        getCartTotal,
      }}
    >
      {children}
      {toastMessage && (
        <div className="wishlist-toast">
          {toastMessage}
        </div>
      )}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

